import React, { useState, useCallback, useEffect } from 'react';
import {
  Upload,
  X,
  Image as ImageIcon,
  Trash2,
  Copy,
  Check,
  Search,
  Grid,
  List,
  Download,
  Eye,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';

interface Asset {
  id: number;
  filename: string;
  original_name: string;
  file_type: string;
  mime_type: string;
  file_size: number;
  url: string;
  thumbnail_url?: string;
  medium_url?: string;
  metadata?: any;
  uploaded_at: string;
}

interface AssetManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (asset: Asset) => void;
  allowMultiple?: boolean;
  fileTypes?: string[];
}

const API_BASE_URL = import.meta.env.PROD
  ? 'https://admin.clarityofconcept.com/api'
  : 'http://localhost:8000';

// Mirror api.ts axios interceptor: pull the admin token from localStorage
// and include it as a Bearer token on outgoing requests. Without this, the
// raw fetch() calls below send no Authorization header and the backend
// rejects them with 401/403 once auth is enforced server-side.
function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = localStorage.getItem('admin_token');
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
}

export const AssetManager: React.FC<AssetManagerProps> = ({
  isOpen,
  onClose,
  onSelect,
  allowMultiple = false,
  fileTypes = ['image']
}) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedAssets, setSelectedAssets] = useState<Set<number>>(new Set());
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAssets();
    }
  }, [isOpen]);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/homepage/assets/list?limit=100`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setAssets(data.assets || []);
      }
    } catch (error) {
      toast.error('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  }, []);

  const handleUpload = async (file: File) => {
    // Validate file type
    const ext = file.name.split('.').pop()?.toLowerCase();
    const validExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'];

    if (!validExtensions.includes(ext || '')) {
      toast.error('Invalid file type. Please upload an image.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE_URL}/homepage/assets/upload`, {
        method: 'POST',
        body: formData,
        headers: authHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Asset uploaded successfully');
        fetchAssets(); // Refresh the list

        // Auto-select if in selection mode
        if (onSelect && data.asset_id) {
          const newAsset: Asset = {
            id: data.asset_id,
            filename: file.name,
            original_name: file.name,
            file_type: 'image',
            mime_type: file.type,
            file_size: file.size,
            url: data.url,
            thumbnail_url: data.thumbnail,
            medium_url: data.medium,
            uploaded_at: new Date().toISOString()
          };
          onSelect(newAsset);
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload asset');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (assetId: number) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/homepage/assets/${assetId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });

      if (response.ok) {
        toast.success('Asset deleted');
        setAssets(assets.filter(a => a.id !== assetId));
      }
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const copyUrl = (url: string, assetId: number) => {
    navigator.clipboard.writeText(url);
    setCopiedId(assetId);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('URL copied to clipboard');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredAssets = assets.filter(asset =>
    asset.original_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectAsset = (asset: Asset) => {
    if (allowMultiple) {
      const newSelection = new Set(selectedAssets);
      if (newSelection.has(asset.id)) {
        newSelection.delete(asset.id);
      } else {
        newSelection.add(asset.id);
      }
      setSelectedAssets(newSelection);
    } else {
      if (onSelect) {
        onSelect(asset);
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Asset Manager</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              />
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white dark:bg-gray-600' : ''}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white dark:bg-gray-600' : ''}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
              }}
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              {uploading ? (
                <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-gray-400" />
              ) : (
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              )}
              <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
                {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Supports: JPG, PNG, WebP, GIF, SVG (max 10MB)
              </p>
            </label>
          </div>
        </div>

        {/* Assets Grid/List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <ImageIcon className="w-16 h-16 mb-4" />
              <p className="text-lg">No assets found</p>
              <p className="text-sm mt-2">Upload your first asset to get started</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredAssets.map((asset) => (
                <div
                  key={asset.id}
                  className={`group relative border rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-lg ${
                    selectedAssets.has(asset.id)
                      ? 'border-blue-500 ring-2 ring-blue-500'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                  onClick={() => handleSelectAsset(asset)}
                >
                  {/* Thumbnail */}
                  <div className="aspect-square bg-gray-100 dark:bg-gray-700">
                    <img
                      src={asset.thumbnail_url || asset.url}
                      alt={asset.original_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  {/* Overlay Actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewAsset(asset);
                      }}
                      className="p-2 bg-white rounded-lg hover:bg-gray-100"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyUrl(asset.url, asset.id);
                      }}
                      className="p-2 bg-white rounded-lg hover:bg-gray-100"
                    >
                      {copiedId === asset.id ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(asset.id);
                      }}
                      className="p-2 bg-white rounded-lg hover:bg-gray-100"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>

                  {/* Info */}
                  <div className="p-2">
                    <p className="text-xs truncate text-gray-700 dark:text-gray-300">
                      {asset.original_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(asset.file_size)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAssets.map((asset) => (
                <div
                  key={asset.id}
                  className={`flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                    selectedAssets.has(asset.id)
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                  onClick={() => handleSelectAsset(asset)}
                >
                  {/* Thumbnail */}
                  <img
                    src={asset.thumbnail_url || asset.url}
                    alt={asset.original_name}
                    className="w-16 h-16 object-cover rounded"
                  />

                  {/* Info */}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {asset.original_name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatFileSize(asset.file_size)} • {new Date(asset.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyUrl(asset.url, asset.id);
                      }}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                    >
                      {copiedId === asset.id ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(asset.id);
                      }}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with selection actions */}
        {allowMultiple && selectedAssets.size > 0 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {selectedAssets.size} asset{selectedAssets.size !== 1 ? 's' : ''} selected
            </p>
            <button
              onClick={() => {
                const selected = assets.filter(a => selectedAssets.has(a.id));
                if (onSelect && selected.length > 0) {
                  selected.forEach(asset => onSelect(asset));
                  onClose();
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Use Selected
            </button>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewAsset && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-8"
          onClick={() => setPreviewAsset(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={previewAsset.url}
              alt={previewAsset.original_name}
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setPreviewAsset(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetManager;